// App.js
import { useEffect, useRef, useState } from 'react';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useReactiveVar, useLazyQuery } from '@apollo/client';
import { alertQueueRepo, authTableTreeRepo, codeNameRepo, codeTreeRepo, dcInfoListRepo, userInfoRepo } from './value/AppModel';
import { AppObject, AppWord } from './value/AppConst';
import AppMain from './com/app_main/AppMain';
import AppLoading from './com/app_loading/AppLoading';
import AppLogin from './com/login/AppLogin';
import AppFooter from './com/appfoot/AppFooter';
import VzmAlert from './common/VzmAlert';
import { GET_SESSION_DATA } from './com/login/AppLoginGql';
import UserSessionInfo from './value/UserSessionInfo';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import MapMain from './com/app_main/MapMain';
import { CODE_TREE_AND_AUTH_TABLE } from './com/common_code/CommonCodeGql';
import { makeAuthTableToTree } from './com/auth_table/AuthTableGql';
import TsrMain from './com/tsr/TsrMain';

const PLEASE_READ_AGAIN = '브라우저 다시 읽기(F5)를 해 주세요.';

const NoAlert = {open:false, alertInfo:null, onClose: null};
const MainAppMode = {Main:0, Map:1, Tsr:2};

function App() {
    const [checkError, setCheckError] = useState(null);
    const sessionInfo = useReactiveVar(userInfoRepo);
    const alertQueue = useReactiveVar(alertQueueRepo);
    const [alertNow, setAlertNow] = useState({...NoAlert});
    const snackbarRef = useRef(null);
    const startRef = useRef(0);
    const gotCodesRef = useRef(0);

    const AppModeNow
    = window.location.href.indexOf(AppWord.MAP_SUB_URL) > 1 ? MainAppMode.Map
    : window.location.href.indexOf(AppWord.TSR_PRINT_SUB_URL) > 1 ? MainAppMode.Tsr
    : MainAppMode.Main;

    const [checkSession, responseCheck] = useLazyQuery(GET_SESSION_DATA,
        {
            ...AppObject.NoCachedFetch,
            onCompleted: (data, option) => {onCompleteCheckSession(data, option)},
            onError: (error) => {setCheckError(error)},
        }
    );

    // CODE_TREE_AND_AUTH_TABLE: code, authTable, dcInfo
    const [getCodeTreeAndAuthTable, responseCodeAndAuth] = useLazyQuery(CODE_TREE_AND_AUTH_TABLE,
        {
            ...AppObject.NoCachedFetch,
            onCompleted: (data, option) => {
                if(data.commonCodeTree && data.authTableList && data.dcInfoList) {
                    const codeNames = {};
                    data.commonCodeTree.forEach((group) => {
                        const map = {__groupName: group.codeGpNm,};
                        group.codes.forEach((code) => {
                            map[code.codeId] = code.codeName;
                        });
                        codeNames[group.codeGp] = map;
                    });

                    codeTreeRepo(data.commonCodeTree)
                    codeNameRepo(codeNames);

                    const secNow = new Date().getTime()/1000;

                    const tree = makeAuthTableToTree(data.authTableList);
                    authTableTreeRepo({when:secNow, tree});

                    dcInfoListRepo({when:secNow, records:data.dcInfoList});
                }
                else if(!data.authTableList) {
                    alert({title:'권한정보 조회 오류', message:[PLEASE_READ_AGAIN]});
                }
                else if(!data.commonCodeTree || !data.dcInfoList) {
                    alert('필수정보 조회 오류:'+PLEASE_READ_AGAIN);
                }
            },
            onError: (error) => {
                alert("필수정보 조회 오류\n" + error.message + "\n" + PLEASE_READ_AGAIN);
            },
        }
    );

    useEffect(() => {
        if(sessionInfo.signedIn) {
            if(gotCodesRef.current === 0) {
                getCodeTreeAndAuthTable();
                gotCodesRef.current = 1;
            }
        }
    }, [sessionInfo, getCodeTreeAndAuthTable]);

    useEffect(() => {
        if(startRef.current === 0) {
            document.title = 'Homeplus NewTOP ' + process.env.REACT_APP_VERSION;
            checkSession();
            startRef.current = 1;
        }
    }, []);

    useEffect(()=>{
        if(alertQueue.length > 0) {
            const alertInfo = alertQueue[0];
            const onClose = () => {
                const newQueue = alertQueue.slice(1);
                setAlertNow({...NoAlert});
                alertQueueRepo(newQueue);
            };
            setAlertNow({open:true, alertInfo, onClose});
        }
    }, [alertQueue]);

    const onCompleteCheckSession = (data, option) => {
        if(data.getSessionData) {
            const sessionInfo = UserSessionInfo.initFromUserObject(data.getSessionData);
            userInfoRepo(sessionInfo);
        }
    };

    const onCompleteLogin = (sessionInfo) => {
        userInfoRepo(sessionInfo);
    };

    if (process.env.NODE_ENV !== 'production') {
        // Adds messages only in a dev environment
        loadDevMessages();
        loadErrorMessages();
    }

    /*
    View cases
    1. checkError ==> message to reload
    2. sessionInfo.sessionChecked === false ==> loading
    3. sessionInfo.sessionChecked === true and sessionInfo.signedIn === false ==> <AppLogin/>
    4. sessionInfo.sessionChecked === true and sessionInfo.signedIn === true ==> <AppMain/>
                <button onClick={() => closeSnackbar(snackbarId)}>
                  닫기
                </button>
    */

    if(checkError) {
        return (
            <AppLoading
                messages={[ "로그인 상태 점검중 오류가 발생했습니다.", "잠시 후 다시 시도해 주세요."]}
            />
        );
    }
    if(sessionInfo.sessionChecked === false) {
        return <AppLoading />
    }


    const renderMain = () => {
        if(sessionInfo.signedIn) {
            if(AppModeNow===MainAppMode.Map) return <MapMain />;
            if(AppModeNow===MainAppMode.Tsr) return <TsrMain />;
            else return <AppMain />;
        }
        return <AppLogin onLoginSuccess={onCompleteLogin}/>;
    };

    return (
        <SnackbarProvider
            ref={snackbarRef}
            disableWindowBlurListener={true}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            action={(snackbarId) => (
                <IconButton
                    size={AppWord.SMALL}
                    onClick={() => closeSnackbar(snackbarId)}
                >
                    <CloseIcon fontSize={AppWord.SMALL}/>
                </IconButton>
            )}
        >
            {renderMain()}
            {AppModeNow===MainAppMode.Main && <AppFooter/>}
            <VzmAlert
                open={alertNow.open}
                alertInfo={alertNow.alertInfo}
                onClose={alertNow.onClose}
            />
        </SnackbarProvider>
    );
}

export default App;

/*
http-proxy-middleware 2.0.6을 사용해야 아래 설정이 가능함.
그렇지 않으면 yarn start했을 때 3000번 포트 서버가 실행되지 않음.

// setupProxy.js
const {createProxyMiddleware} = require("http-proxy-middleware");


module.exports = (app) => {
   app.use(
       createProxyMiddleware('/graphql', {
           target: 'http://localhost:3355',
           changeOrigin: true,
       })
   );

    // Proxy middleware for /download requests. 이 설정을 더한 후 yarn start를 다시 해야 함.
    app.use(
        '/download',
        createProxyMiddleware({
            target: 'http://localhost:3355',
            changeOrigin: true,
        })
    );
};


*/